import { DOCUMENT, Inject, inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { WINDOW } from '@df/dom/globals';
import { Renderer } from '@df/ng/renderer';
import { BehaviorSubject, fromEvent, merge, Subscription } from 'rxjs';
import { HeadService } from '../meta/head/head.service';
import { MultisiteService } from './multisite';

/**
 * Inject atomic stylesheets files and add custom classes to the document body.
 *
 * @export
 * @class StylesService
 */
@Injectable({
    providedIn: 'root'
})
export class StylesService {
    // protected version: string;

    readonly scale$ = new BehaviorSubject<number>(1);

    private initiated = false;

    /**
     * Last set of values written by setViewportSizeVars, used to skip redundant writes.
     */
    private lastViewportSizeVars?: string;

    /**
     * Handle of the animation frame pending for setViewportSizeVars.
     */
    private viewportSizeVarsFrame?: number;
    /**
     * Provides value in vw units that can safely be used on a website
     * in client app
     * 12 will be converted to 12vw
     * in admin app
     * 12 will be converted to calc(var(--vw)*12)
     */
    static vw(value: number): string {
        return `calc(var(--vw, 1vw)*${value})`;
    }
    static vh(value: number): string {
        return `calc(var(--vh, 1vh)*${value})`;
    }

    protected subscriptions = new Subscription();

    private window = inject(WINDOW);

    constructor(
        private renderer: Renderer,
        private client: Client,
        private multisite: MultisiteService,
        private app: App,
        private headService: HeadService,
        @Inject(DOCUMENT) private document: Document
    ) {}

    appInitialize() {
        const init = () => {
            this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.setSizeClass()));
            this.subscriptions.add(this.client.obs.resize.subscribe(() => this.scheduleViewportSizeVars()));

            if (this.app.isPlatformBrowser) {
                this.subscriptions.add(
                    merge(fromEvent(window as Window, 'resize'), fromEvent(window as Window, 'DOMContentLoaded'), this.scale$).subscribe(
                        () => this.scheduleViewportSizeVars()
                    )
                );
            }

            this.initiated = true;

            this.setViewportSizeVars();
            this.setSizeClass();
            this.setCustomClass();
        };

        const linkElement = this.headService.findElement('#style-styles');
        if (linkElement && linkElement instanceof HTMLLinkElement && !linkElement.getAttribute('ready')) {
            linkElement.addEventListener('load', init);
            linkElement.addEventListener('error', init);
        } else {
            init();
        }
    }

    ngOnDestroy() {
        if (typeof this.viewportSizeVarsFrame !== 'undefined') {
            this.window.cancelAnimationFrame(this.viewportSizeVarsFrame);
            this.viewportSizeVarsFrame = undefined;
        }
        this.subscriptions.unsubscribe();
        this.scale$.complete();
    }

    protected setSCrollTop() {
        this.subscriptions.add(
            this.client.obs.scroll.subscribe(() => {
                this.setCssVar('--scrollTop', `${this.client.getRootElement().scrollTop || 0}px`);
            })
        );
    }

    /**
     * Set custom classes.
     *
     * @memberof StylesService
     */
    private setCustomClass() {
        // set touchy related classes
        const isTouchy = this.client.isTouchy;

        this.renderer.addClass(this.document.body, isTouchy ? 'is-touchy' : 'is-not-touchy');
        this.renderer.removeClass(this.document.body, isTouchy ? 'is-not-touchy' : 'is-touchy');
        if (this.multisite.isRtl) {
            this.renderer.addClass(this.document.body, 'rtl');
        }
    }

    /**
     * Updates size class on body element.
     *
     * @protected
     * @memberof StylesService
     */
    private setSizeClass() {
        if (!this.client.size) {
            return;
        }
        const appSize: string = this.client.size.toLowerCase();
        ['s', 'm', 'l', 'x'].forEach(size => {
            this.renderer.setClass(this.document.body, 'size-' + size, size === appSize);
        });
    }

    /**
     * Coalesces viewport size var updates into a single animation frame, so the layout
     * read happens once per frame in the rendering phase instead of synchronously inside
     * every event handler, where it forces a reflow of the whole document.
     */
    private scheduleViewportSizeVars() {
        if (
            !this.initiated ||
            !this.app.isPlatformBrowser ||
            this.client.isPrerender ||
            typeof this.viewportSizeVarsFrame !== 'undefined'
        ) {
            return;
        }

        this.viewportSizeVarsFrame = this.window.requestAnimationFrame(() => {
            this.viewportSizeVarsFrame = undefined;
            this.setViewportSizeVars();
        });
    }

    private setViewportSizeVars() {
        if (this.initiated && this.app.isPlatformBrowser && !this.client.isPrerender) {
            const isCms = this.client.isCms;
            const scale = this.scale$.value;
            const windowHeight = this.window.innerHeight;
            // client.height/width are only needed by the cms app, reading them outside of it
            // costs a layout read for a value that is thrown away
            const vh = `${Math.round((isCms ? Math.min(windowHeight, this.client.height) : windowHeight) / scale) / 100}px`;
            const vw = isCms ? `${Math.round(this.client.width / scale) / 100}px` : '1vw';
            const windowHeightVar = `min(100vh, ${windowHeight}px)`;

            // the vars live on the root element, so rewriting them invalidates the style of
            // every element using them - only touch them when a value actually changed
            const values = `${vh}|${vw}|${windowHeightVar}`;
            if (values === this.lastViewportSizeVars) {
                return;
            }
            this.lastViewportSizeVars = values;

            this.setCssVar('--vh', vh);
            this.setCssVar('--vw', vw);
            this.setCssVar('--window-height', windowHeightVar);
        }
    }

    setCssVar(name: string, value: string | undefined, target: HTMLElement = this.document.documentElement) {
        if (typeof value === 'undefined') {
            target.style.removeProperty(name);
        } else {
            target.style.setProperty(name, value);
        }
    }
}
